import { useState } from "react";
import { Mail, Check, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * HeroNewsletterCard — compact inline Sunday-with-Pip signup that sits
 * directly under the homepage hero buttons. Designed to match the cream
 * hero background with soft sage borders — visible without being loud.
 */
export default function HeroNewsletterCard() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { name, email });
      setDone(true);
      toast.success("Welcome — see you Sunday.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't sign you up. Try again?"
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-8 bg-white/70 border border-sage/30 rounded-2xl p-6 flex items-center gap-4 max-w-xl"
        data-testid="hero-newsletter-success"
      >
        <div className="inline-flex w-11 h-11 items-center justify-center rounded-full bg-sage text-cream flex-shrink-0">
          <Check size={18} strokeWidth={1.8} />
        </div>
        <div>
          <div className="font-serif-display text-xl text-ink leading-tight">
            You're in.
          </div>
          <div className="text-sm text-ink-soft mt-1">
            See you Sunday morning, {name ? name.split(" ")[0] : "lovely"}.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-8 bg-white/70 backdrop-blur border border-sage/25 rounded-2xl p-6 md:p-7 max-w-xl shadow-[0_1px_0_rgba(74,93,79,0.04)]"
      data-testid="hero-newsletter-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-sage/10 text-sage flex-shrink-0">
          <Mail size={16} strokeWidth={1.8} />
        </div>
        <div className="leading-tight">
          <div className="eyebrow text-sage">Free weekly newsletter</div>
          <div className="font-serif-display text-lg md:text-xl text-ink">
            Sunday with Pip
          </div>
        </div>
      </div>
      <p className="text-sm text-ink-soft leading-relaxed mb-5">
        One quiet email each Sunday morning — an observation, a small invitation, a reminder that you're allowed to slow down.
      </p>
      <form
        onSubmit={submit}
        className="grid grid-cols-1 sm:grid-cols-5 gap-2"
        data-testid="hero-newsletter-form"
      >
        <input
          required
          type="text"
          name="first_name"
          autoComplete="given-name"
          placeholder="First name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="sm:col-span-2 bg-white border border-hairline rounded-full px-4 py-3 text-ink placeholder-ink-soft/50 text-sm focus:outline-none focus:border-sage transition"
          data-testid="hero-newsletter-name"
        />
        <input
          required
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@yours.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="sm:col-span-2 bg-white border border-hairline rounded-full px-4 py-3 text-ink placeholder-ink-soft/50 text-sm focus:outline-none focus:border-sage transition"
          data-testid="hero-newsletter-email"
        />
        <button
          type="submit"
          disabled={loading}
          className="sm:col-span-1 inline-flex items-center justify-center gap-1.5 bg-sage hover:bg-sage-dark text-cream rounded-full px-4 py-3 font-medium text-sm transition-all disabled:opacity-50"
          data-testid="hero-newsletter-submit"
        >
          {loading ? "…" : "Subscribe"}
          {!loading && <ArrowRight size={14} strokeWidth={1.8} />}
        </button>
      </form>
      <p className="mt-3 text-xs text-ink-soft/70">
        No sales pitch. Unsubscribe anytime.
      </p>
    </div>
  );
}
